import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  IoArrowBack, IoArrowForward, IoSparkles, IoCreateOutline, IoClose, IoSaveOutline,
  IoTrashOutline, IoAddCircleOutline, IoImageOutline, IoTextOutline, IoArrowUp, IoArrowDown, IoCloudUploadOutline,
} from "react-icons/io5";
import DojoShell, { RED } from "@/components/DojoShell";
import { useAuth } from "@/context/AuthContext";
import { getPatchNote, updatePatchNote, deletePatchNote, uploadPatchImage, patchImageUrl } from "@/lib/api";

const INTERNAL_LINKS = [
  { value: "", label: "Aucun bouton" },
  { value: "/ecole", label: "L'École" },
  { value: "/bibliotheque", label: "La Bibliothèque" },
  { value: "/bibliotheque/dictionnaire", label: "Le Dictionnaire" },
  { value: "/carnet", label: "Mon Carnet" },
  { value: "/communaute", label: "La Communauté" },
  { value: "/accompagnement", label: "L'Accompagnement" },
  { value: "/membres", label: "Les Membres" },
  { value: "/hub", label: "Le Hub" },
];

function fmt(iso) {
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch { return ""; }
}
function toDateInput(iso) {
  try { return new Date(iso).toISOString().slice(0, 10); } catch { return ""; }
}
const uid = () => Math.random().toString(36).slice(2, 10);

export default function PatchNote() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { isAdmin } = useAuth();
  const [patch, setPatch] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const coverInput = useRef(null);

  useEffect(() => {
    getPatchNote(id).then((p) => {
      setPatch(p);
      if (sp.get("edit") === "1" && isAdmin) { setDraft(JSON.parse(JSON.stringify(p))); setEditing(true); }
    }).catch(() => { toast.error("Nouveauté introuvable"); nav("/nouveautes"); });
    // eslint-disable-next-line
  }, [id]);

  const openEdit = () => { setDraft(JSON.parse(JSON.stringify(patch))); setEditing(true); };
  const closeEdit = () => { setEditing(false); setSp({}, { replace: true }); };

  const save = async () => {
    setBusy(true);
    try {
      const saved = await updatePatchNote(id, { ...draft, date: draft.date || null });
      setPatch(saved); setEditing(false); setSp({}, { replace: true });
      toast.success("Nouveauté enregistrée");
    } catch { toast.error("Échec de l'enregistrement"); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer définitivement cette nouveauté ?")) return;
    try { await deletePatchNote(id); toast.success("Supprimé"); nav("/nouveautes"); } catch { toast.error("Échec"); }
  };

  // ----- block helpers (edit) -----
  const setBlock = (bid, k, v) => setDraft((d) => ({ ...d, blocks: d.blocks.map((b) => (b.id === bid ? { ...b, [k]: v } : b)) }));
  const addBlock = (type) => setDraft((d) => ({ ...d, blocks: [...(d.blocks || []), { id: uid(), type, content: "", caption: "" }] }));
  const delBlock = (bid) => setDraft((d) => ({ ...d, blocks: d.blocks.filter((b) => b.id !== bid) }));
  const moveBlock = (idx, dir) => setDraft((d) => {
    const arr = [...d.blocks]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return d;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...d, blocks: arr };
  });

  const doUpload = async (file, onDone) => {
    if (!file) return;
    try {
      const res = await uploadPatchImage(file);
      onDone(patchImageUrl(res.image_id));
    } catch { toast.error("Échec de l'upload de l'image"); }
  };

  const uploadBlockImage = async (bid, file) => {
    setUploadingId(bid);
    await doUpload(file, (url) => setBlock(bid, "content", url));
    setUploadingId(null);
  };
  const uploadCover = async (file) => {
    setUploadingId("cover");
    await doUpload(file, (url) => setDraft((d) => ({ ...d, cover: url })));
    setUploadingId(null);
  };

  if (!patch) return <DojoShell><p style={{ color: "var(--text-secondary)" }}>Chargement…</p></DojoShell>;

  const view = editing ? draft : patch;
  const ctaLabelDefault = INTERNAL_LINKS.find((l) => l.value === view?.cta_link)?.label;

  return (
    <DojoShell crumbs={[{ label: "Nouveautés", to: "/nouveautes" }, { label: patch.title }]}>
      <div style={s.topbar}>
        <button style={s.backBtn} data-testid="patch-back" onClick={() => nav("/nouveautes")}><IoArrowBack /> Toutes les nouveautés</button>
        {isAdmin && !editing && (
          <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
            <button style={s.editBtn} data-testid="patch-edit-open" onClick={openEdit}><IoCreateOutline size={16} /> Modifier</button>
            <button style={{ ...s.editBtn, color: RED, borderColor: RED }} data-testid="patch-delete" onClick={remove}><IoTrashOutline size={16} /> Supprimer</button>
          </div>
        )}
      </div>

      {!editing ? (
        <article style={s.article} data-testid="patch-article">
          {view.cover && <div style={{ ...s.heroCover, backgroundImage: `url(${view.cover})` }} />}
          <div style={s.metaRow}>
            <span style={s.dateBig}><IoSparkles size={14} /> {fmt(view.date || view.created_at)}</span>
            {!view.published && <span style={s.draftTag}>Brouillon (visible par toi uniquement)</span>}
          </div>
          <h1 style={s.title} data-testid="patch-view-title">{view.title}</h1>
          {view.summary && <p style={s.summary}>{view.summary}</p>}

          <div style={s.blocks}>
            {(view.blocks || []).map((b) => (
              b.type === "image" ? (
                b.content ? (
                  <figure key={b.id} style={s.figure}>
                    <img src={b.content} alt={b.caption || ""} style={s.img} />
                    {b.caption && <figcaption style={s.caption}>{b.caption}</figcaption>}
                  </figure>
                ) : null
              ) : (
                b.content ? (
                  <div key={b.id} style={s.textBlock}>
                    {String(b.content).split("\n").map((line, i) => (
                      line.trim() ? <p key={i} style={s.para}>{line}</p> : <br key={i} />
                    ))}
                  </div>
                ) : null
              )
            ))}
          </div>

          {view.cta_link && (
            <button style={s.cta} data-testid="patch-cta" onClick={() => { const l = view.cta_link; if (/^https?:\/\//.test(l)) window.open(l, "_blank"); else nav(l); }}>
              {view.cta_label || `Aller vers ${ctaLabelDefault || "l'espace"}`} <IoArrowForward size={18} />
            </button>
          )}
        </article>
      ) : (
        <div style={s.editor} data-testid="patch-editor">
          <div style={s.editHead}>
            <span style={s.editHeadTitle}>Mode Création — Patch</span>
            <button style={s.closeX} data-testid="patch-edit-cancel" onClick={closeEdit}><IoClose size={20} /></button>
          </div>

          <label style={s.lbl}>Titre</label>
          <input style={s.input} data-testid="patch-field-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Titre de la nouveauté" />

          <label style={s.lbl}>Date</label>
          <input type="date" style={s.input} data-testid="patch-field-date" value={toDateInput(draft.date || draft.created_at)} onChange={(e) => setDraft({ ...draft, date: e.target.value ? new Date(e.target.value).toISOString() : null })} />

          <label style={s.lbl}>Résumé (une ou deux phrases, affiché dans la liste)</label>
          <textarea style={{ ...s.input, resize: "vertical" }} rows={2} data-testid="patch-field-summary" value={draft.summary || ""} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} placeholder="Ce que change cette nouveauté, en bref." />

          <label style={s.lbl}>Image de couverture</label>
          <div style={s.coverEdit}>
            {draft.cover ? <div style={{ ...s.coverThumb, backgroundImage: `url(${draft.cover})` }} /> : <div style={s.coverThumbEmpty}><IoImageOutline size={24} /></div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
              <input ref={coverInput} type="file" accept="image/*" style={{ display: "none" }} data-testid="patch-cover-input" onChange={(e) => uploadCover(e.target.files?.[0])} />
              <button style={s.uploadBtn} onClick={() => coverInput.current?.click()} data-testid="patch-cover-upload"><IoCloudUploadOutline size={16} /> {uploadingId === "cover" ? "Envoi…" : "Téléverser une image"}</button>
              {draft.cover && <button style={s.linkBtn} onClick={() => setDraft({ ...draft, cover: "" })}>Retirer</button>}
            </div>
          </div>

          <div style={s.divider} />
          <label style={s.lbl}>Contenu du patch (blocs)</label>
          <div style={s.blockEditList}>
            {(draft.blocks || []).map((b, idx) => (
              <div key={b.id} style={s.blockEdit} data-testid={`patch-block-${idx}`}>
                <div style={s.blockBar}>
                  <span style={s.blockKind}>{b.type === "image" ? <><IoImageOutline size={13} /> Image</> : <><IoTextOutline size={13} /> Texte</>}</span>
                  <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
                    <button style={s.miniBtn} data-testid={`patch-block-up-${idx}`} onClick={() => moveBlock(idx, -1)}><IoArrowUp size={14} /></button>
                    <button style={s.miniBtn} data-testid={`patch-block-down-${idx}`} onClick={() => moveBlock(idx, 1)}><IoArrowDown size={14} /></button>
                    <button style={{ ...s.miniBtn, color: RED }} data-testid={`patch-block-del-${idx}`} onClick={() => delBlock(b.id)}><IoTrashOutline size={14} /></button>
                  </div>
                </div>
                {b.type === "image" ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {b.content && <div style={{ ...s.coverThumb, width: "100%", height: 150, backgroundImage: `url(${b.content})` }} />}
                    <label style={s.uploadBtn} data-testid={`patch-block-img-upload-${idx}`}>
                      <IoCloudUploadOutline size={16} /> {uploadingId === b.id ? "Envoi…" : (b.content ? "Remplacer l'image" : "Téléverser une image")}
                      <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => uploadBlockImage(b.id, e.target.files?.[0])} />
                    </label>
                    <input style={s.input} placeholder="Légende (optionnel)" value={b.caption || ""} onChange={(e) => setBlock(b.id, "caption", e.target.value)} />
                  </div>
                ) : (
                  <textarea style={{ ...s.input, resize: "vertical" }} rows={4} placeholder="Écris ton paragraphe… (saut de ligne = nouveau paragraphe)" value={b.content} data-testid={`patch-block-text-${idx}`} onChange={(e) => setBlock(b.id, "content", e.target.value)} />
                )}
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
            <button style={s.addBlockBtn} data-testid="patch-add-text" onClick={() => addBlock("text")}><IoTextOutline size={15} /> Ajouter un texte</button>
            <button style={s.addBlockBtn} data-testid="patch-add-image" onClick={() => addBlock("image")}><IoImageOutline size={15} /> Ajouter une image</button>
          </div>

          <div style={s.divider} />
          <label style={s.lbl}>Bouton d'action (vers un espace du Dōjō)</label>
          <select style={s.input} data-testid="patch-field-link" value={draft.cta_link || ""} onChange={(e) => setDraft({ ...draft, cta_link: e.target.value })}>
            {INTERNAL_LINKS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
          {draft.cta_link && (
            <input style={s.input} data-testid="patch-field-cta-label" placeholder="Texte du bouton (ex: Découvrir la nouveauté)" value={draft.cta_label || ""} onChange={(e) => setDraft({ ...draft, cta_label: e.target.value })} />
          )}

          <div style={s.divider} />
          <label style={s.toggleRow} data-testid="patch-field-published">
            <input type="checkbox" checked={!!draft.published} onChange={(e) => setDraft({ ...draft, published: e.target.checked })} />
            <span><strong>Publié</strong> — visible par tous les membres (sinon brouillon privé)</span>
          </label>

          <div style={s.editActions}>
            <button style={s.cancelBtn} onClick={closeEdit}>Annuler</button>
            <button style={{ ...s.saveBtn, opacity: busy ? 0.6 : 1 }} disabled={busy} data-testid="patch-save" onClick={save}><IoSaveOutline size={16} /> {busy ? "Enregistrement…" : "Enregistrer"}</button>
          </div>
        </div>
      )}
    </DojoShell>
  );
}

const s = {
  topbar: { display: "flex", alignItems: "center", gap: 10, marginBottom: 18, flexWrap: "wrap" },
  backBtn: { display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 14px", borderRadius: 999, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontWeight: 700, fontSize: 13, cursor: "pointer" },
  editBtn: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 999, border: `1.5px solid var(--border-c)`, background: "var(--bg-secondary)", color: "var(--text-primary)", fontWeight: 800, fontSize: 13, cursor: "pointer" },
  article: { maxWidth: 760, margin: "0 auto" },
  heroCover: { width: "100%", height: 300, borderRadius: 20, backgroundSize: "cover", backgroundPosition: "center", marginBottom: 22, boxShadow: "var(--surface-shadow)" },
  metaRow: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 10 },
  dateBig: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 800, letterSpacing: 0.5, color: RED, textTransform: "uppercase" },
  draftTag: { fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 999, background: "var(--bg-tertiary)", color: "var(--text-secondary)" },
  title: { fontSize: 40, fontWeight: 900, margin: "0 0 14px", fontFamily: "var(--font-display)", color: "var(--text-primary)", lineHeight: 1.1 },
  summary: { fontSize: 18, lineHeight: 1.6, color: "var(--text-secondary)", margin: "0 0 26px", fontStyle: "italic" },
  blocks: { display: "flex", flexDirection: "column", gap: 22 },
  textBlock: { display: "flex", flexDirection: "column", gap: 14 },
  para: { fontSize: 17, lineHeight: 1.75, color: "var(--text-primary)", margin: 0 },
  figure: { margin: 0 },
  img: { width: "100%", borderRadius: 16, display: "block", boxShadow: "var(--surface-shadow)" },
  caption: { fontSize: 13, color: "var(--text-secondary)", textAlign: "center", marginTop: 8, fontStyle: "italic" },
  cta: { display: "inline-flex", alignItems: "center", gap: 9, marginTop: 34, padding: "15px 26px", borderRadius: 999, border: "none", background: RED, color: "#fff", fontWeight: 900, fontSize: 16, cursor: "pointer", boxShadow: `0 14px 34px color-mix(in srgb, var(--brand) 38%, transparent)` },

  editor: { maxWidth: 760, margin: "0 auto", padding: 22, borderRadius: 18, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", display: "flex", flexDirection: "column", gap: 8, boxShadow: "var(--surface-shadow)" },
  editHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 },
  editHeadTitle: { fontSize: 16, fontWeight: 900, color: "var(--text-primary)" },
  closeX: { width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-secondary)", cursor: "pointer" },
  lbl: { fontSize: 12.5, fontWeight: 800, color: "var(--text-secondary)", marginTop: 8 },
  input: { width: "100%", padding: "11px 13px", border: "1.5px solid var(--border-c)", borderRadius: 10, fontSize: 14.5, fontFamily: "inherit", color: "var(--text-primary)", background: "var(--bg-tertiary)", boxSizing: "border-box" },
  coverEdit: { display: "flex", gap: 14, alignItems: "flex-start" },
  coverThumb: { width: 140, height: 90, flexShrink: 0, borderRadius: 12, backgroundSize: "cover", backgroundPosition: "center", border: "1px solid var(--border-c)" },
  coverThumbEmpty: { width: 140, height: 90, flexShrink: 0, borderRadius: 12, border: "1px dashed var(--border-c)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-secondary)", background: "var(--bg-tertiary)" },
  uploadBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${RED}`, background: "transparent", color: RED, fontWeight: 800, fontSize: 13, cursor: "pointer" },
  linkBtn: { background: "none", border: "none", color: "var(--text-secondary)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", textAlign: "left", padding: 0, textDecoration: "underline" },
  divider: { height: 1, background: "var(--border-c)", margin: "14px 0 6px" },
  blockEditList: { display: "flex", flexDirection: "column", gap: 12 },
  blockEdit: { padding: 12, borderRadius: 12, border: "1px solid var(--border-c)", background: "var(--bg-tertiary)", display: "flex", flexDirection: "column", gap: 8 },
  blockBar: { display: "flex", alignItems: "center", gap: 8 },
  blockKind: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 800, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: 0.5 },
  miniBtn: { width: 30, height: 30, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 7, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-secondary)", cursor: "pointer" },
  addBlockBtn: { flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "12px", borderRadius: 10, border: "1.5px dashed var(--border-c)", background: "var(--bg-tertiary)", color: "var(--text-primary)", fontWeight: 800, fontSize: 13, cursor: "pointer" },
  toggleRow: { display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "var(--text-primary)", cursor: "pointer" },
  editActions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 },
  cancelBtn: { padding: "11px 18px", borderRadius: 10, border: "1.5px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-secondary)", fontWeight: 700, cursor: "pointer" },
  saveBtn: { display: "inline-flex", alignItems: "center", gap: 7, padding: "11px 22px", borderRadius: 10, border: "none", background: RED, color: "#fff", fontWeight: 900, cursor: "pointer" },
};
